import { useState, useEffect, useCallback, useRef } from "react";
import { useNavigate } from "react-router-dom";
import NCAHTLogo from "../assets/images/NCAHTlogo.png";
import ICMRLogo from "../assets/images/ICMRlogo.png";
import NISHLogo from "../assets/images/NISHlogo.png";

/**
 * PublicQuickScreener.jsx — NISH FSLP / Vidyagni
 * ─────────────────────────────────────────────────────────────────────────
 * Public, unauthenticated, route-based version of the 10-activity
 * Panchakosha Quick Screener. No login, no children list, no backend save.
 *
 * EXAM-STYLE FLOW: activities are presented one at a time, sliding left/right
 * like a question carousel, with Prev/Next navigation and a 10-dot progress
 * tracker. Time spent on each activity is tracked silently (never shown on
 * screen) and is embedded as a hidden note in the printed report so NISH
 * staff can spot unusually fast completions that may indicate the parent
 * prompted the child rather than observing naturally.
 *
 * The take-home report now includes a full cluster analysis (11 functional
 * skill clusters spanning all 50 signals), strengths/developing/needs-support
 * groupings, auto-generated recommendations, and home activity suggestions —
 * mirroring the cluster-analysis system used in the full 90-item SRT reports.
 * ─────────────────────────────────────────────────────────────────────────
 */

const P = {
  navy: "#0B1F35", ink: "#1A2B3C", muted: "#637080", border: "#E0E8F0",
  fog: "#F4F7FA", white: "#FFFFFF",
  green: "#1A7A40", greenBg: "#E8F5EE",
  amber: "#B06000", amberBg: "#FEF3E2",
  red: "#C0392B", redBg: "#FDECEA",
  sky: "#1A6FAA", gold: "#C8A84B",
};

const CSS = `
  @import url('https://fonts.googleapis.com/css2?family=Outfit:wght@400;600;700;800;900&family=Lora:ital,wght@0,600;0,700;1,600&display=swap');
  *, *::before, *::after { box-sizing: border-box; }
  body { margin: 0; }
  @keyframes pqs-up    { from{opacity:0;transform:translateY(16px)} to{opacity:1;transform:translateY(0)} }
  @keyframes pqs-in    { from{opacity:0;transform:scale(.97)} to{opacity:1;transform:scale(1)} }
  @keyframes pqs-slide-right { from{opacity:0;transform:translateX(48px)} to{opacity:1;transform:translateX(0)} }
  @keyframes pqs-slide-left  { from{opacity:0;transform:translateX(-48px)} to{opacity:1;transform:translateX(0)} }
  .pqs-up    { animation: pqs-up .45s ease both; }
  .pqs-in    { animation: pqs-in .25s ease both; }
  .pqs-slide-right { animation: pqs-slide-right .32s cubic-bezier(.2,.8,.2,1) both; }
  .pqs-slide-left  { animation: pqs-slide-left .32s cubic-bezier(.2,.8,.2,1) both; }
`;
function injectCSS() {
  if (document.getElementById("pqs-css")) return;
  const s = document.createElement("style");
  s.id = "pqs-css"; s.textContent = CSS;
  document.head.appendChild(s);
}

// ─── Domain definitions (same as internal QuickScreener) ───────────────────
const DOMAINS = [
  { id: "annamaya", label: "Annamaya", full: "Annamaya Kosha", subtitle: "Physical & Motor", icon: "🏃", color: "#0E8A7A", bg: "#E6F5F3", dark: "#0A6E62" },
  { id: "pranamaya", label: "Pranamaya", full: "Pranamaya Kosha", subtitle: "Socio-Emotional", icon: "💛", color: "#C2620A", bg: "#FEF0E6", dark: "#A0520A" },
  { id: "manomaya", label: "Manomaya", full: "Manomaya Kosha", subtitle: "Mind & Cognition", icon: "🧠", color: "#6E3EA6", bg: "#F3EEF9", dark: "#5A318A" },
  { id: "vijnanamaya", label: "Vijnanamaya", full: "Vijnanamaya Kosha", subtitle: "Language, Literacy & Numeracy", icon: "📚", color: "#1A4F7A", bg: "#EBF2FA", dark: "#153E60" },
  { id: "anandamaya", label: "Anandamaya", full: "Anandamaya Kosha", subtitle: "Joy, Play & Creativity", icon: "🎨", color: "#B03060", bg: "#FCE8F0", dark: "#8A1E48" },
];

// ─── The 10 activities (identical definitions/scoring to internal screener) ─
const ACTIVITIES = [
  { id: "an-1", domain: "annamaya", seq: 1, title: "The Drawing Challenge", duration: "2 min", icon: "✏️",
    tagline: "Draw yourself, then trace this path ⭐ ——→ 🏠",
    setup: "Place a sheet of paper with a dotted star-to-house path on one side. Give the child a crayon.",
    instruction: "Draw a picture of yourself on this side. Then follow the dotted path on the other side with your finger or crayon.",
    what_to_watch: "Observe grip, pressure, posture, eye-hand coordination, figure completeness, and line-following accuracy simultaneously.",
    signals: [
      { id: "an1-grip", label: "Pencil/crayon grip", hint: "Mature tripod vs palmar/fist grasp", weight: 3 },
      { id: "an1-figure", label: "Person drawing complexity", hint: "Scribble only / head-only / 2-3 parts / full figure", weight: 3 },
      { id: "an1-trace", label: "Can follow a dotted path", hint: "Able to track and follow a visual line", weight: 2 },
      { id: "an1-posture", label: "Appropriate seated posture", hint: "Core stability, bilateral coordination, trunk control", weight: 1 },
      { id: "an1-shapes", label: "Attempts or copies any shapes", hint: "Does the drawing contain intentional closed forms", weight: 2 },
    ] },
  { id: "an-2", domain: "annamaya", seq: 2, title: "Move Like Me", duration: "2 min", icon: "🤸",
    tagline: "Jump, balance, clap-pattern, throw to hoop",
    setup: "Clear a small floor space. Place a hoop or chalk circle ~1.5m away. Have a soft ball or beanbag ready.",
    instruction: "Let's do some fun moves together! Jump on both feet — now balance on one foot — now copy my clap: clap… clap-clap… can you throw the ball into that circle?",
    what_to_watch: "Four micro-tasks in one sequence. Each is observable in ~20 seconds. Note which the child attempts, avoids, or succeeds at.",
    signals: [
      { id: "an2-jump", label: "Jumps with both feet & lands", hint: "Both feet leave ground; landing is controlled or unsteady", weight: 3 },
      { id: "an2-balance", label: "Balances on one foot briefly", hint: "Even 2–3 seconds counts; note if refused", weight: 2 },
      { id: "an2-rhythm", label: "Copies clap rhythm pattern", hint: "AB clap pattern; exact match vs approximation vs no response", weight: 2 },
      { id: "an2-throw", label: "Throws with directional aim", hint: "Any throw toward target; doesn't need to land in hoop", weight: 2 },
      { id: "an2-engage", label: "Engages willingly or needs coaxing", hint: "Willingness to participate in physical activity", weight: 1 },
    ] },
  { id: "pr-1", domain: "pranamaya", seq: 3, title: "The Feelings Puppet Show", duration: "2 min", icon: "🎭",
    tagline: "Puppet shows emotions — child responds, identifies, acts",
    setup: "Use any small puppet or soft toy. Have 3 emotion picture cards (happy, sad, scared) visible.",
    instruction: "Make the puppet show 3 emotions one at a time: happy (clap & smile), sad (hang head), scared (cover eyes). Ask: How does the puppet feel now? How do YOU feel when you're scared? Then ask the child to make the puppet show happy.",
    what_to_watch: "Watch for emotion recognition, labelling, empathic response, communication mode, and willingness to play-act.",
    signals: [
      { id: "pr1-recog", label: "Identifies 2+ basic emotions", hint: "Happy, sad, scared — verbal, pointing, or AAC", weight: 3 },
      { id: "pr1-label", label: "Labels emotion with a word/sign", hint: "Uses any communication mode to name the emotion", weight: 3 },
      { id: "pr1-empathy", label: "Shows empathic response to puppet", hint: "Leans in, says 'poor puppet', offers comfort", weight: 2 },
      { id: "pr1-pretend", label: "Makes puppet express an emotion", hint: "Any pretend/symbolic puppet use", weight: 2 },
      { id: "pr1-express", label: "Expresses own feeling verbally", hint: "Names or shows how they feel in a similar scenario", weight: 2 },
    ] },
  { id: "pr-2", domain: "pranamaya", seq: 4, title: "The Sharing Game", duration: "2 min", icon: "🤝",
    tagline: "Turn-taking ball roll, sticker sharing, waiting in line",
    setup: "A soft ball and a sheet of stickers. Two chairs facing each other ~1.5m apart.",
    instruction: "Roll the ball back and forth 4 times ('your turn… my turn…'). Then say: I have stickers — let's share them with everyone. Finally: Let's line up for our next activity — wait for me to say go.",
    what_to_watch: "Three sub-tasks test turn-taking, generosity, and impulse control in a natural play context.",
    signals: [
      { id: "pr2-turns", label: "Waits for their turn (ball roll)", hint: "No reminders / 1 reminder / constant reminders / refuses", weight: 3 },
      { id: "pr2-share", label: "Shares stickers without resistance", hint: "Willing / brief hesitation / refuses / only under pressure", weight: 3 },
      { id: "pr2-wait", label: "Waits in line calmly", hint: "Stays in place without bolting or constant fidgeting", weight: 2 },
      { id: "pr2-rules", label: "Follows 2-step game instruction", hint: "Sit → roll; wait → go — follows both parts", weight: 2 },
      { id: "pr2-repair", label: "Responds if ball rolls past them", hint: "Retrieves and returns (cooperative); ignores (avoidant)", weight: 1 },
    ] },
  { id: "mn-1", domain: "manomaya", seq: 5, title: "The Sorting Tray", duration: "2 min", icon: "🗂️",
    tagline: "Sort animals/vehicles, extend a pattern, find the odd one",
    setup: "6 picture cards (3 animals, 3 vehicles) mixed in a pile. A row of 4 coloured blocks: Red Blue Red Blue. 4 more picture cards for odd-one-out (3 fruit + 1 bus).",
    instruction: "Sort these into animals and vehicles. Then: What colour block comes next? Then show 4 cards: Three go together — which one is different?",
    what_to_watch: "Three sub-tasks in 2 minutes. Sorting shows classification; pattern continuation shows sequencing; odd-one-out shows logical grouping.",
    signals: [
      { id: "mn1-sort", label: "Correctly sorts all into 2 groups", hint: "All 6 correct / 4-5 / 2-3 / random", weight: 3 },
      { id: "mn1-pattern", label: "Extends AB colour pattern", hint: "Says or places Blue; needs prompt; incorrect; no attempt", weight: 3 },
      { id: "mn1-oddone", label: "Identifies the odd one out", hint: "Picks bus (non-fruit); can explain why", weight: 3 },
      { id: "mn1-focus", label: "Sustains focus across all 3 sub-tasks", hint: "Completes all / needs redirection / only 1-2 tasks", weight: 2 },
      { id: "mn1-explain", label: "Gives a reason for their choice", hint: "'Because it's not food' — any verbal justification", weight: 2 },
    ] },
  { id: "mn-2", domain: "manomaya", seq: 6, title: "The Memory Sequence", duration: "2 min", icon: "🧩",
    tagline: "3-item recall, 3-step instruction, story retell (3 events)",
    setup: "3 small objects (e.g. toy car, pencil, spoon). A simple 3-sentence story to read aloud.",
    instruction: "Show 3 objects for 10 sec, hide them: What did you see? Then give a 3-step instruction: Stand up, walk to the door, come back and sit. Then read: Riya woke up. She ate breakfast. She went to school. Ask: Tell me what happened.",
    what_to_watch: "Three memory/sequence tasks back-to-back reveal working memory, instruction-following depth, and narrative sequencing.",
    signals: [
      { id: "mn2-recall", label: "Recalls 2+ of 3 hidden objects", hint: "3/3 strong; 2/3 developing; 0-1 emerging", weight: 3 },
      { id: "mn2-3step", label: "Follows all 3-step instruction", hint: "All 3 / first 2 / first 1 / no follow", weight: 3 },
      { id: "mn2-retell", label: "Retells story in correct order", hint: "All 3 events / 2 events / 1 event / no recall", weight: 3 },
      { id: "mn2-sequence", label: "Uses sequence words (first/then)", hint: "'First she woke up, then…' — any ordering language", weight: 2 },
      { id: "mn2-attend", label: "Listens to story without prompting", hint: "Attends to 3 sentences without redirection", weight: 1 },
    ] },
  { id: "vj-1", domain: "vijnanamaya", seq: 7, title: "The Number & Letter Hunt", duration: "2 min", icon: "🔢",
    tagline: "Count stars, bigger number, letter hunt, first sound",
    setup: "A card with ⭐⭐⭐⭐ (4 stars). A card showing '6' and '3'. A card with letters A B M S T. Say the word 'SUN' clearly.",
    instruction: "Count these stars and tap the number card (3/4/5/6). Then: Which number is bigger — 6 or 3? Then: Point to the letter A… now M… now S. Then: What's the FIRST sound in the word… SUN?",
    what_to_watch: "Four numeracy/literacy micro-tasks spanning counting, quantity comparison, letter recognition, and phonological awareness.",
    signals: [
      { id: "vj1-count", label: "Counts 4 objects correctly", hint: "Points and counts 1-to-1; identifies correct numeral card", weight: 3 },
      { id: "vj1-bigger", label: "Identifies bigger of two numbers", hint: "6 > 3 — correct / incorrect / no response", weight: 3 },
      { id: "vj1-letters", label: "Recognises 3+ printed letters", hint: "Points correctly to A, M, S from a set of 5", weight: 3 },
      { id: "vj1-sound", label: "Identifies first sound of 'SUN'", hint: "/s/ — correct / approximate / whole word / no response", weight: 3 },
      { id: "vj1-1to1", label: "1-to-1 counting correspondence", hint: "Touches each star once while counting — no skipping", weight: 2 },
    ] },
  { id: "vj-2", domain: "vijnanamaya", seq: 8, title: "The Story Picture", duration: "2 min", icon: "📖",
    tagline: "Describe a scene, make a sentence, write/spell own name",
    setup: "A simple scene picture (home/park/school). A blank paper and crayon for name writing.",
    instruction: "Look at this picture — tell me EVERYTHING you see. Then: Make a sentence using the word 'school'. Then: Can you write your name? Or spell it out loud?",
    what_to_watch: "Language depth, vocabulary range, sentence construction, and early literacy from one scene description task.",
    signals: [
      { id: "vj2-describe", label: "Describes full scene (not just labels)", hint: "Narrative / partial scene / isolated labels / no response", weight: 3 },
      { id: "vj2-sentence", label: "Forms a grammatical sentence", hint: "Full sentence / 2-word phrase / single word / no attempt", weight: 3 },
      { id: "vj2-vocab", label: "Uses varied vocabulary (5+ words)", hint: "Count distinct meaningful words in the description", weight: 2 },
      { id: "vj2-name", label: "Writes or spells own name", hint: "Legible / recognisable attempt / partial / scribble / cannot", weight: 3 },
      { id: "vj2-ordinal", label: "Uses any position/sequence language", hint: "'The boy is next to…', 'first there's a…'", weight: 1 },
    ] },
  { id: "aa-1", domain: "anandamaya", seq: 9, title: "The Creation Station", duration: "2 min", icon: "🎨",
    tagline: "Free draw anything, design a flag, describe it",
    setup: "Blank paper, crayons in 4+ colours. No instructions on what to draw.",
    instruction: "Draw anything you want — it can be real or made-up. There's no wrong answer! After 90 seconds: Now design your own flag using colours and shapes. Then: Tell me about what you made.",
    what_to_watch: "Observe creative intent, colour use, motor control, imaginative engagement, and verbal description quality.",
    signals: [
      { id: "aa1-intent", label: "Draws with clear creative intent", hint: "Random marks / purposeful scribble / recognisable content", weight: 3 },
      { id: "aa1-explains", label: "Describes what they made", hint: "Detailed description / brief / names only / no response", weight: 3 },
      { id: "aa1-colour", label: "Chooses and uses colours deliberately", hint: "Selects specific colours for purpose (not random)", weight: 2 },
      { id: "aa1-flag", label: "Creates a distinct flag design", hint: "Any intentional design vs random marks", weight: 2 },
      { id: "aa1-engage", label: "Shows visible joy or absorption", hint: "Smiling, humming, fully absorbed in creative task", weight: 2 },
    ] },
  { id: "aa-2", domain: "anandamaya", seq: 10, title: "The Imagination Round", duration: "2 min", icon: "🚀",
    tagline: "Favourite animal + story, sing/hum, invent something",
    setup: "No materials needed. This is a conversational + movement activity.",
    instruction: "What's your favourite animal? Tell me one thing about it and do its movement! Then hum or clap a simple melody and invite them to join. Finally: If you could invent ANYTHING, what would you make?",
    what_to_watch: "Three tiny tasks reveal self-expression, musical responsiveness, body representation, and imaginative fluency.",
    signals: [
      { id: "aa2-animal", label: "Names animal + shares a fact or feeling", hint: "Name+fact+emotion / name+fact / name only / no response", weight: 3 },
      { id: "aa2-move", label: "Imitates the animal's movement", hint: "Full-body / partial / gesture / refused", weight: 2 },
      { id: "aa2-music", label: "Joins in with music (hum/clap)", hint: "Sings/hums / claps rhythm / slight response / no response", weight: 2 },
      { id: "aa2-invent", label: "Describes an imaginary invention", hint: "Detailed / brief / single word / no response", weight: 3 },
      { id: "aa2-story", label: "Adds any story/narrative to animal", hint: "'My elephant loves to swim and…' — any extension", weight: 2 },
    ] },
];

const SIGNAL_LEVELS = [
  { id: "strong", label: "Strong", short: "✓✓", desc: "Clear, independent", score: 2, color: "#1A7A40", bg: "#E8F5EE" },
  { id: "present", label: "Present", short: "✓", desc: "With minor support", score: 1, color: "#1A6FAA", bg: "#EBF4FA" },
  { id: "emerging", label: "Emerging", short: "~", desc: "Attempted, inconsistent", score: 0, color: "#B06000", bg: "#FEF3E2" },
  { id: "absent", label: "Absent", short: "✗", desc: "Not observed / refused", score: -1, color: "#C0392B", bg: "#FDECEA" },
];

// Lookup table: signal id -> weight (used by cluster scoring)
const SIGNAL_WEIGHT = {};
ACTIVITIES.forEach(act => act.signals.forEach(s => { SIGNAL_WEIGHT[s.id] = s.weight; }));

// ─── Skill clusters — group the 50 signals into 11 functional areas ────────
// (mirrors the cluster-analysis approach used in the full 90-item SRT report,
// scaled down to the 10-activity quick screener)
const QS_CLUSTERS = [
  { id: "gross_motor", label: "Gross Motor & Coordination", icon: "🏃", color: "#0E8A7A", bg: "#E6F5F3",
    desc: "Large-muscle movement, balance, rhythm and ball skills",
    items: ["an2-jump", "an2-balance", "an2-rhythm", "an2-throw", "an1-posture"] },
  { id: "fine_motor", label: "Fine Motor & Tool Use", icon: "✏️", color: "#1A4F7A", bg: "#EBF2FA",
    desc: "Pencil grip, line-tracing and controlled hand movements",
    items: ["an1-grip", "an1-trace", "an1-shapes", "aa1-colour", "vj2-name"] },
  { id: "visual_expression", label: "Drawing & Visual Representation", icon: "🎨", color: "#B03060", bg: "#FCE8F0",
    desc: "Representing people and ideas through drawing",
    items: ["an1-figure", "aa1-intent", "aa1-flag", "aa1-explains"] },
  { id: "emotional_awareness", label: "Emotional Awareness & Empathy", icon: "💛", color: "#C2620A", bg: "#FEF0E6",
    desc: "Recognising, naming and responding to feelings",
    items: ["pr1-recog", "pr1-label", "pr1-empathy", "pr1-express"] },
  { id: "social_regulation", label: "Social Skills & Self-Regulation", icon: "🤝", color: "#A0522D", bg: "#FBEEE6",
    desc: "Turn-taking, sharing, waiting and following play rules",
    items: ["pr2-turns", "pr2-share", "pr2-wait", "pr2-rules", "pr2-repair", "an2-engage"] },
  { id: "cognitive_reasoning", label: "Cognitive Reasoning & Classification", icon: "🧠", color: "#6E3EA6", bg: "#F3EEF9",
    desc: "Sorting, pattern recognition and logical grouping",
    items: ["mn1-sort", "mn1-pattern", "mn1-oddone", "mn1-explain", "mn1-focus"] },
  { id: "memory_sequencing", label: "Memory & Sequencing", icon: "🧩", color: "#5D4037", bg: "#EFEBE9",
    desc: "Working memory, instruction-following and ordering events",
    items: ["mn2-recall", "mn2-3step", "mn2-retell", "mn2-sequence", "mn2-attend"] },
  { id: "numeracy", label: "Early Numeracy", icon: "🔢", color: "#B45309", bg: "#FFFBEB",
    desc: "Counting, one-to-one correspondence and number comparison",
    items: ["vj1-count", "vj1-bigger", "vj1-1to1"] },
  { id: "literacy", label: "Emergent Literacy", icon: "📖", color: "#0D5C63", bg: "#E8F8F5",
    desc: "Letter recognition and phonological awareness",
    items: ["vj1-letters", "vj1-sound"] },
  { id: "language", label: "Language & Communication", icon: "🗣️", color: "#1A4F7A", bg: "#EBF2FA",
    desc: "Vocabulary, sentence formation and descriptive talk",
    items: ["vj2-describe", "vj2-sentence", "vj2-vocab", "vj2-ordinal", "pr1-pretend"] },
  { id: "creativity", label: "Creativity & Imagination", icon: "🚀", color: "#0891B2", bg: "#ECFEFF",
    desc: "Imaginative play, music and inventive thinking",
    items: ["aa1-engage", "aa2-animal", "aa2-move", "aa2-music", "aa2-invent", "aa2-story"] },
];

// Narrative + home-activity content for each cluster, keyed by performance band
const CLUSTER_DETAIL = {
  gross_motor: {
    achieved: "Shows confident large-muscle control — jumps and lands, balances briefly, follows movement patterns and throws with direction. Ready for active outdoor play and group games.",
    developing: "Attempts jumping, balancing and throwing, but landings may be unsteady, balance brief, or aim inconsistent. These skills typically firm up quickly with regular practice.",
    emerging: "Large-muscle coordination is still forming — the child may avoid jumping, lose balance immediately, or not yet copy a clap rhythm. A physiotherapy or occupational therapy opinion can help build a foundation.",
    activities: [
      { title: "Animal Walks", desc: "Hop like a rabbit, waddle like a duck, stomp like an elephant across the room — builds balance, strength and body awareness through play." },
      { title: "Bucket Toss", desc: "Place a bucket or basket at increasing distances and practise underarm throws with a soft ball — builds aim and shoulder control." },
    ] },
  fine_motor: {
    achieved: "Holds a pencil with good control, traces lines accurately and manages small tools confidently — a strong foundation for handwriting and self-care tasks.",
    developing: "Can attempt drawing and colouring tasks, but grip may still be immature or lines may wander outside the path — typical at this stage and improves with practice.",
    emerging: "Hand control for drawing and tracing is still developing. The child may use a fist or palmar grip, or be unable to follow a simple path. Occupational therapy input on fine-motor readiness is recommended.",
    activities: [
      { title: "Playdough Pinch & Roll", desc: "Roll small balls and pinch flat 'pancakes' with thumb and first two fingers — builds the pincer grip needed for a pencil." },
      { title: "Sticker Transfer Game", desc: "Peel stickers off a sheet and stick them inside outlined shapes — strengthens finger isolation and precision." },
    ] },
  visual_expression: {
    achieved: "Draws people with multiple recognisable body parts and can describe and explain their own creations with confidence.",
    developing: "Attempts to draw a person and explain their work, though figures may still be simple (head and a couple of parts) — a normal step on the way to fuller figure drawings.",
    emerging: "Drawing is mostly at the scribble stage with little intentional representation. Encouraging mark-making and modelling simple figure drawing together can help.",
    activities: [
      { title: "Build-a-Person Together", desc: "Draw a big circle for the head and ask 'what goes next?' — add eyes, arms and legs one at a time, naming each part." },
      { title: "My Day in 3 Pictures", desc: "Each evening, draw one quick picture of something that happened that day and talk about it — builds representational drawing and recall together." },
    ] },
  emotional_awareness: {
    achieved: "Recognises and names common emotions in others and can talk about their own feelings — a strong base for self-regulation and friendships.",
    developing: "Can identify one or two emotions and respond with some empathy, but naming feelings or describing their own emotional state may need prompting.",
    emerging: "Recognising and naming emotions in self and others is still early. Using picture cards and naming feelings often during the day can build this skill steadily.",
    activities: [
      { title: "Feelings Faces Mirror", desc: "Make happy, sad and scared faces in a mirror together and name each one — repeat daily for a minute." },
      { title: "Story Character Feelings", desc: "While reading any picture book, pause and ask 'how do you think they feel right now?'" },
    ] },
  social_regulation: {
    achieved: "Waits for a turn, shares willingly and follows simple play rules without much reminding — ready for cooperative group activities.",
    developing: "Participates in turn-taking and sharing games but may need a reminder or two, or show brief hesitation before sharing — typical, and improves with consistent practice.",
    emerging: "Turn-taking, sharing and waiting are still hard for the child right now. Short, frequent practice in low-pressure play (rather than high-stakes moments) builds this gradually.",
    activities: [
      { title: "Two-Minute Turn Timer", desc: "Use a simple sand timer during shared play — 'your turn until the sand runs out, then mine.'" },
      { title: "Sharing Basket Game", desc: "Put a few favourite small toys in a basket and take turns choosing one, modelling 'my turn… your turn' language." },
    ] },
  cognitive_reasoning: {
    achieved: "Sorts items accurately, extends simple patterns and can identify and explain why one item doesn't belong — strong early logical thinking.",
    developing: "Manages some sorting and pattern tasks correctly but may need a prompt for the odd-one-out or pattern continuation — an emerging skill at this age.",
    emerging: "Sorting, pattern and grouping tasks are still difficult. Everyday sorting games (laundry, cutlery, toys by colour) build this skill naturally.",
    activities: [
      { title: "Sock Sorting", desc: "Mix clean socks and ask the child to pair and sort them by colour or pattern — a real-life sorting task." },
      { title: "Pattern Train", desc: "Line up coloured blocks or beads in a repeating pattern (red-blue-red-blue) and ask 'what comes next?'" },
    ] },
  memory_sequencing: {
    achieved: "Recalls multiple items, follows multi-step instructions, and retells short stories in order — strong working memory for early learning tasks.",
    developing: "Can recall some items or follow part of an instruction sequence, but may lose track on the third item or step — typically strengthens with repetition.",
    emerging: "Holding information in mind and recalling sequences is an area to build. Short games with 1–2 items/steps, gradually increasing, will help.",
    activities: [
      { title: "What's Missing? Game", desc: "Place 3 toys on a tray, cover them, remove one — ask 'what's missing?' Build up to more items as confidence grows." },
      { title: "Simple Simon Says", desc: "Give one instruction at a time ('touch your toes'), then build to two-step instructions ('touch your toes, then clap')." },
    ] },
  numeracy: {
    achieved: "Counts accurately with one-to-one correspondence and compares quantities confidently — well prepared for early number work.",
    developing: "Counts some objects correctly and attempts quantity comparisons, though small errors (skipping or double-counting) may occur — normal at this stage.",
    emerging: "Counting and number comparison are still forming. Counting real objects during daily routines (steps, snacks, toys) builds this naturally.",
    activities: [
      { title: "Snack Counting", desc: "Count out snack pieces one by one onto a plate, touching each one while counting aloud." },
      { title: "Which Has More?", desc: "Place two small groups of objects (buttons, grapes) side by side and ask 'which group has more?'" },
    ] },
  literacy: {
    achieved: "Recognises several letters and identifies beginning sounds in words — ready to begin connecting sounds and symbols for reading.",
    developing: "Recognises a few letters or attempts sound identification with some support — typical pre-reading development.",
    emerging: "Letter recognition and sound awareness are just beginning. Pointing out letters in the child's name and environment, and playing simple sound games, builds this foundation.",
    activities: [
      { title: "Letter Hunt at Home", desc: "Find and circle the letters of the child's name on cereal boxes, signs or magazines." },
      { title: "I Spy a Sound", desc: "'I spy something that starts with /b/…' — make it a game during daily routines like bath time or car rides." },
    ] },
  language: {
    achieved: "Describes scenes in detail, forms full sentences and uses varied vocabulary — strong expressive language for classroom participation.",
    developing: "Communicates in short phrases or simple sentences and can describe parts of a scene — vocabulary and sentence length typically grow quickly with rich conversation.",
    emerging: "Expressive language — describing, forming sentences, using varied words — is an area to focus on. Narrating everyday activities out loud and expanding the child's words into fuller sentences helps build this.",
    activities: [
      { title: "Narrate Together", desc: "While doing daily tasks, describe what's happening ('we are washing the red cup') and invite the child to add a word or two." },
      { title: "Picture Walk", desc: "Look through a picture book together and ask the child to describe what's happening on each page, gently expanding their answers into fuller sentences." },
    ] },
  creativity: {
    achieved: "Engages joyfully in imaginative play, music and inventive ideas — shows strong creative thinking and self-expression.",
    developing: "Joins in creative activities and can share a simple idea (favourite animal, an invented object), though elaboration may be brief.",
    emerging: "Imaginative play and creative expression are still developing. Open-ended play materials and modelling 'let's pretend' games can open this up.",
    activities: [
      { title: "Pretend Animal Safari", desc: "Take turns being different animals — moving, sounding and 'living' like them — then talk about what each animal likes." },
      { title: "Invent-a-Thing", desc: "Using a cardboard box or simple objects, ask 'if this could be anything, what would it be?' and build a short story around it." },
    ] },
};

// ─── Parental consent content ───────────────────────────────────────────────
const CONSENT_SECTIONS = [
  {
    title: "About this study",
    body: "This free Quick Screener is offered as part of the Vidyagni FSLP research programme, run by the National Centre for Assistive Health Technology (NCAHT) at the National Institute of Speech and Hearing (NISH), under the Indian Council of Medical Research (ICMR). It is a research study that assesses school readiness for children aged 3–8 (including children with special needs) across physical, socio-emotional, cognitive, language, literacy, numeracy, and creative domains, in line with NCF-FS 2022 and NEP 2020. Findings may be used for academic publications, presentations, and policy reporting."
  },
  {
    title: "What's involved",
    body: "Your child will not be present for any procedure here — instead, you (the parent/guardian) observe your child doing 10 short, playful activities at home or in a familiar setting and rate what you see. This takes about 15–20 minutes. There are no invasive procedures, blood samples, or physical interventions of any kind, and no investigations are performed."
  },
  {
    title: "Data storage & confidentiality",
    body: "If you choose to save or submit your results, responses are recorded on the Vidyagni FSLP platform and stored securely for up to five years in line with ICMR data retention guidelines. Your information is strictly confidential — no personally identifiable information (name, photos, audio, or video) will be used in any publication or presentation. Data is accessed only by authorised members of the research team and is never shared with third parties without your explicit consent."
  },
  {
    title: "Risks, benefits & costs",
    body: "There are no risks involved in this screening, and no compensation is provided. There is no cost to participate. You may gain a better understanding of your child's school readiness and receive guidance for educational planning."
  },
  {
    title: "Your rights",
    body: "Participation is entirely voluntary. You may choose not to participate, withdraw at any time, or request deletion of your child's data, without any penalty and without affecting any services you currently receive. By checking the box below, you confirm that you have read this information, understand it, and voluntarily agree to participate in this free screening on behalf of your child."
  },
];

// ─── Scoring (identical logic to internal screener) ─────────────────────────
function computeResult(ratings) {
  const domainScores = {};
  DOMAINS.forEach(d => {
    const acts = ACTIVITIES.filter(a => a.domain === d.id);
    let total = 0, maxTotal = 0;
    acts.forEach(act => {
      act.signals.forEach(sig => {
        const lv = ratings[sig.id];
        if (lv) {
          const lvDef = SIGNAL_LEVELS.find(l => l.id === lv);
          total += sig.weight * (lvDef?.score ?? 0);
          maxTotal += sig.weight * 2;
        } else {
          maxTotal += sig.weight * 2;
        }
      });
    });
    const shifted = total + maxTotal;
    const maxShifted = 2 * maxTotal;
    domainScores[d.id] = maxShifted > 0 ? Math.round((shifted / maxShifted) * 100) : 50;
  });

  const overall = Math.round(Object.values(domainScores).reduce((a, b) => a + b, 0) / DOMAINS.length);

  const flags = {};
  DOMAINS.forEach(d => {
    const s = domainScores[d.id];
    flags[d.id] = s >= 65 ? "green" : s >= 42 ? "amber" : "red";
  });

  const redCount = Object.values(flags).filter(f => f === "red").length;
  const amberCount = Object.values(flags).filter(f => f === "amber").length;

  let recommendation;
  if (redCount >= 2) recommendation = "full-srt";
  else if (redCount === 1 || amberCount >= 3) recommendation = "monitor";
  else recommendation = "no-concerns";

  return { domainScores, overall, flags, recommendation };
}

// ─── Cluster scoring ──────────────────────────────────────────────────────
// Returns 0–100 for a cluster, or null if none of its signals were rated yet.
function clusterScore(cluster, ratings) {
  const rated = cluster.items.filter(id => ratings[id]);
  if (rated.length === 0) return null;
  let sum = 0, max = 0;
  rated.forEach(id => {
    const w = SIGNAL_WEIGHT[id] || 1;
    const lvDef = SIGNAL_LEVELS.find(l => l.id === ratings[id]);
    sum += w * ((lvDef?.score ?? 0) + 1); // shift -1..2 -> 0..3
    max += w * 3;
  });
  return Math.round((sum / max) * 100);
}

function computeClusterScores(ratings) {
  const out = {};
  QS_CLUSTERS.forEach(c => { out[c.id] = clusterScore(c, ratings); });
  return out;
}

function levelFor(pct) {
  if (pct === null || pct === undefined) return { label: "Not Assessed", color: "#9CA3AF", bg: "#F3F4F6" };
  if (pct >= 70) return { label: "Strong", color: P.green, bg: P.greenBg };
  if (pct >= 40) return { label: "Developing", color: P.amber, bg: P.amberBg };
  return { label: "Needs Support", color: P.red, bg: P.redBg };
}

// ─── Recommendations (domain + cluster aware) ───────────────────────────────
function buildRecommendations(result, clusterScores) {
  const { flags, recommendation } = result;
  const recs = [];

  if (recommendation === "no-concerns") {
    recs.push("Overall development looks age-appropriate across all five Panchakosha areas observed today. Continue everyday play and learning, and re-screen in about 6 months.");
  } else if (recommendation === "monitor") {
    recs.push("One or more areas show emerging skills. Continue regular practice at home using the suggested activities below, and consider a re-screen in 8–10 weeks.");
  } else {
    recs.push("Two or more areas show limited or delayed skills today. We recommend sharing this report with your child's teacher or a special educator, and considering the full 90-item Panchakosha assessment through Vidyagni / NISH for a detailed developmental profile.");
  }

  DOMAINS.forEach(d => {
    if (flags[d.id] === "red") {
      recs.push(`Priority focus area: ${d.label} (${d.full}) — this domain showed limited skills in today's screening.`);
    }
  });

  const weakClusters = QS_CLUSTERS
    .map(c => ({ ...c, pct: clusterScores[c.id] }))
    .filter(c => c.pct !== null && c.pct < 40)
    .sort((a, b) => a.pct - b.pct);

  weakClusters.forEach(c => {
    recs.push(`Consider extra everyday practice in ${c.label} (${c.icon}) — see the home activity suggestions in this report.`);
  });

  const notAssessed = QS_CLUSTERS.filter(c => clusterScores[c.id] === null);
  if (notAssessed.length) {
    recs.push(`${notAssessed.map(c => c.label).join(", ")} — not enough signals were rated to assess this area. Consider completing all 10 activities for a fuller picture.`);
  }

  return recs;
}

function calcAge(dob) {
  if (!dob) return null;
  return Math.floor((new Date() - new Date(dob)) / 31557600000);
}

// ─── Hidden timing notes (never shown to the parent — for internal review) ──
function buildTimingNotes(activityTimes) {
  const notes = [];
  ACTIVITIES.forEach(act => {
    const secs = activityTimes[act.id];
    if (secs === undefined) {
      notes.push({ activity: act.title, seconds: 0, flag: "not_visited" });
    } else if (secs < 15) {
      notes.push({ activity: act.title, seconds: Math.round(secs), flag: "rapid_completion" });
    } else {
      notes.push({ activity: act.title, seconds: Math.round(secs), flag: "ok" });
    }
  });
  return notes;
}

// ─── Printable take-home report ─────────────────────────────────────────────
function printScreeningReport(child, ratings, result, clusterScores, date, activityTimes) {
  const { domainScores, overall, flags, recommendation } = result;
  const REC = {
    "no-concerns": { label: "✅ No Concerns", color: "#1A7A40", bg: "#E8F5EE",
      text: "Your child shows age-appropriate development across all five areas observed today. No further assessment is needed right now. Re-screen in about 6 months." },
    "monitor": { label: "⚠️ Monitor & Support", color: "#B06000", bg: "#FEF3E2",
      text: "Your child shows emerging skills in one or more areas. Continue everyday practice at home and re-screen in 8–10 weeks. If you'd like a more detailed picture, a full 90-item assessment is available through NISH/Vidyagni." },
    "full-srt": { label: "🔴 Detailed Assessment Recommended", color: "#C0392B", bg: "#FDECEA",
      text: "Your child shows delayed or limited skills in two or more areas observed today. We recommend sharing this report with a special educator or developmental specialist, and considering the full 90-item Panchakosha assessment for a detailed developmental profile." },
  };
  const rec = REC[recommendation];
  const age = child?.dob ? calcAge(child.dob) + "y" : "–";

  const domainRows = DOMAINS.map(d => {
    const sc = domainScores[d.id];
    const fl = flags[d.id];
    const fc = fl === "green" ? "#1A7A40" : fl === "amber" ? "#B06000" : "#C0392B";
    const fb = fl === "green" ? "#E8F5EE" : fl === "amber" ? "#FEF3E2" : "#FDECEA";
    const acts = ACTIVITIES.filter(a => a.domain === d.id);
    const sigRows = acts.flatMap(act =>
      act.signals.map(sig => {
        const lv = ratings[sig.id];
        const lvDef = SIGNAL_LEVELS.find(l => l.id === lv);
        return `<tr>
          <td style="padding:3px 8px;font-size:9px;color:#666">${act.title}</td>
          <td style="padding:3px 8px;font-size:9px">${sig.label}</td>
          <td style="padding:3px 8px;text-align:center">
            ${lvDef
              ? `<span style="background:${lvDef.bg};color:${lvDef.color};font-size:8px;font-weight:700;padding:1px 6px;border-radius:4px">${lvDef.label}</span>`
              : `<span style="color:#aaa;font-size:8px">—</span>`
            }
          </td>
        </tr>`;
      })
    ).join("");

    return `
      <div style="margin-bottom:10px;page-break-inside:avoid">
        <div style="display:flex;justify-content:space-between;align-items:center;background:${fb};border-left:4px solid ${fc};padding:7px 12px;border-radius:0 6px 6px 0;margin-bottom:5px">
          <span style="font-weight:800;font-size:12px;color:${fc}">${d.icon} ${d.full}</span>
          <span style="font-size:9px;color:${fc}">${d.subtitle}</span>
          <span style="font-weight:800;font-size:11px;color:${fc}">${sc}% — ${fl === "green" ? "No concerns" : fl === "amber" ? "Monitor" : "Refer"}</span>
        </div>
        <table style="width:100%;border-collapse:collapse;font-family:Arial,sans-serif">
          <thead><tr style="background:#FAFAFA">
            <th style="padding:3px 8px;text-align:left;font-size:8px;color:#888;width:120px">Activity</th>
            <th style="padding:3px 8px;text-align:left;font-size:8px;color:#888">Signal Observed</th>
            <th style="padding:3px 8px;font-size:8px;color:#888;width:80px">Rating</th>
          </tr></thead>
          <tbody>${sigRows}</tbody>
        </table>
      </div>`;
  }).join("");

  // ── Cluster analysis grid ──────────────────────────────────────────────
  const clusterGridHTML = QS_CLUSTERS.map(c => {
    const pct = clusterScores[c.id];
    const clv = levelFor(pct);
    const ratedCount = c.items.filter(id => ratings[id]).length;
    const bar = pct !== null
      ? `<div style="height:7px;background:#E5E7EB;border-radius:4px;margin:6px 0 4px;overflow:hidden"><div style="width:${pct}%;height:100%;background:${c.color};border-radius:4px"></div></div>`
      : `<div style="height:7px;background:#E5E7EB;border-radius:4px;margin:6px 0 4px"></div>`;
    return `<div style="border:1.5px solid #E5E7EB;border-radius:9px;overflow:hidden;${pct===null?"opacity:.55":""}">
      <div style="height:4px;background:${pct!==null?c.color:"#E5E7EB"}"></div>
      <div style="padding:8px 10px">
        <div style="display:flex;justify-content:space-between;align-items:flex-start;margin-bottom:2px">
          <span style="font-size:11px;font-weight:800;color:${c.color}">${c.icon} ${c.label}</span>
          <span style="font-size:14px;font-weight:900;color:${clv.color}">${pct!==null?pct+"%":"—"}</span>
        </div>
        ${bar}
        <div style="display:flex;justify-content:space-between;align-items:center;margin-top:2px">
          <span style="background:${clv.bg};color:${clv.color};font-size:9px;font-weight:700;padding:1px 7px;border-radius:4px">${clv.label}</span>
          <span style="font-size:9px;color:#9CA3AF">${ratedCount}/${c.items.length} signals</span>
        </div>
      </div>
    </div>`;
  }).join("");

  const assessedClusters = QS_CLUSTERS.map(c => ({ ...c, pct: clusterScores[c.id] })).filter(c => c.pct !== null);
  const strengths  = assessedClusters.filter(c => c.pct >= 70);
  const developing = assessedClusters.filter(c => c.pct >= 40 && c.pct < 70);
  const needs      = assessedClusters.filter(c => c.pct < 40);

  const groupHTML = (title, list, color, bg, emptyText) => `
    <div style="background:${bg};border-radius:10px;padding:12px 14px;border:1.5px solid ${color}44">
      <div style="font-size:11px;font-weight:800;color:${color};margin-bottom:8px">${title} (${list.length})</div>
      ${list.length === 0
        ? `<div style="font-size:11px;color:#6B7280;font-style:italic">${emptyText}</div>`
        : list.map(c => `<div style="font-size:11px;color:${color};font-weight:600;margin-bottom:4px">${c.icon} ${c.label} <span style="color:${color}">(${c.pct}%)</span></div>`).join("")}
    </div>`;

  // ── Recommendations ────────────────────────────────────────────────────
  const recsHTML = buildRecommendations(result, clusterScores).map((r, i) => `
    <div style="display:flex;gap:10px;align-items:flex-start;margin-bottom:9px">
      <div style="min-width:22px;height:22px;border-radius:50%;background:${rec.color};color:white;font-size:11px;font-weight:900;display:flex;align-items:center;justify-content:center;flex-shrink:0">${i+1}</div>
      <p style="margin:0;font-size:12px;color:#1F2937;line-height:1.7;padding-top:2px">${r}</p>
    </div>`).join("");

  // ── Home activities for the lowest-scoring assessed clusters ────────────
  const topNeeds = [...assessedClusters].sort((a, b) => a.pct - b.pct).slice(0, 3);
  const homeActivitiesHTML = topNeeds.map(c => {
    const detail = CLUSTER_DETAIL[c.id];
    if (!detail?.activities?.length) return "";
    return `<div style="background:white;border-radius:10px;padding:12px 14px;border:1.5px solid #D1FAE5;margin-bottom:10px">
      <div style="display:flex;align-items:center;gap:9px;margin-bottom:8px">
        <div style="width:32px;height:32px;border-radius:8px;background:${c.color}18;display:flex;align-items:center;justify-content:center;font-size:16px;flex-shrink:0">${c.icon}</div>
        <div>
          <div style="font-size:12px;font-weight:800;color:${c.color}">${c.label}</div>
          <div style="font-size:10px;color:#6B7280">Current level: ${c.pct}% · ${levelFor(c.pct).label}</div>
        </div>
      </div>
      ${detail.activities.map((act, i) => `
        <div style="display:flex;gap:9px;align-items:flex-start;margin-bottom:7px">
          <div style="min-width:20px;height:20px;border-radius:50%;background:#16A34A;color:white;font-size:10px;font-weight:900;display:flex;align-items:center;justify-content:center;flex-shrink:0">${i+1}</div>
          <div>
            <div style="font-size:11px;font-weight:700;color:#166534;margin-bottom:2px">${act.title}</div>
            <div style="font-size:11px;color:#374151;line-height:1.6">${act.desc}</div>
          </div>
        </div>`).join("")}
    </div>`;
  }).join("");

  // ── Cluster narratives ───────────────────────────────────────────────────
  const clusterNarrativesHTML = assessedClusters.map(c => {
    const detail = CLUSTER_DETAIL[c.id];
    const clv = levelFor(c.pct);
    const levelKey = c.pct >= 70 ? "achieved" : c.pct >= 40 ? "developing" : "emerging";
    const text = detail ? detail[levelKey] : "";
    return `<div style="background:#FAFBFC;border-radius:9px;padding:11px 13px;margin-bottom:9px;border-left:4px solid ${c.color}">
      <div style="display:flex;justify-content:space-between;align-items:center;margin-bottom:5px">
        <span style="font-size:12px;font-weight:800;color:${c.color}">${c.icon} ${c.label}</span>
        <span style="background:${clv.bg};color:${clv.color};font-size:10px;font-weight:700;padding:2px 9px;border-radius:6px">${c.pct}% · ${clv.label}</span>
      </div>
      <p style="font-size:11px;color:#374151;line-height:1.7;margin:0">${text}</p>
    </div>`;
  }).join("");

  // ── Hidden internal timing notes (NOT visible on the rendered page) ────
  const timingNotes = buildTimingNotes(activityTimes);
  const timingComment = `<!-- INTERNAL TIMING NOTES (not shown to parent) — for NISH staff data-quality review.
  Expected ~120s per activity. "rapid_completion" flags activities rated in under 15s,
  which may indicate the parent answered quickly without fully observing/prompting the child.
  ${JSON.stringify(timingNotes)}
  -->`;

  const win = window.open("", "_blank");
  if (!win) return;
  win.document.write(`<!DOCTYPE html><html><head><meta charset="UTF-8"/>
  <style>*{margin:0;padding:0;box-sizing:border-box}body{font-family:'Segoe UI',Arial,sans-serif;padding:20px;color:#182430;font-size:11px}
  tr{border-bottom:1px solid #F5F5F5}@media print{@page{size:A4;margin:8mm}.no-print{display:none!important}.page-break{page-break-before:always}}</style>
  </head><body>
  ${timingComment}
  <div style="border-bottom:3px solid #0B1F35;padding-bottom:10px;margin-bottom:14px;display:flex;justify-content:space-between;align-items:flex-end">
    <div style="display:flex;align-items:center;gap:10px">
      <div style="background:#0B1F35;color:white;font-weight:900;font-size:13px;padding:3px 8px;border-radius:4px">NISH</div>
      <div>
        <div style="font-weight:700;color:#1A4F7A;font-size:9px">National Institute of Speech &amp; Hearing · NCAHT · Vidyagni FSLP</div>
        <div style="font-size:8px;color:#888">Thiruvananthapuram, Kerala</div>
      </div>
    </div>
    <div style="text-align:center">
      <div style="font-size:13px;font-weight:900;color:#0B1F35">Free Panchakosha Quick Screener</div>
      <div style="font-size:8px;color:#888;margin-top:1px">10-Activity Take-Home Screening · NCF-FS 2022</div>
    </div>
    <div style="display:flex;gap:4px">
      <div style="background:#b30000;color:white;font-size:7px;font-weight:700;padding:2px 6px;border-radius:3px">NCAHT</div>
      <div style="background:#006400;color:white;font-size:7px;font-weight:700;padding:2px 6px;border-radius:3px">ICMR</div>
    </div>
  </div>

  <div style="display:grid;grid-template-columns:1fr 1fr;gap:12px;margin-bottom:14px">
    <div style="background:#F8F9FC;border-radius:8px;padding:12px">
      <div style="font-size:9px;color:#888;font-weight:700;margin-bottom:6px">CHILD PROFILE</div>
      ${[["Name", child?.name || "–"], ["Age", age], ["Notes", child?.notes || "–"], ["Screening Date", date], ["Activities Completed", "10 of 10"]].map(([k, v]) => `<div style="display:flex;gap:8px;padding:2px 0;border-bottom:1px solid #EEE;font-size:9px"><span style="color:#888;min-width:100px">${k}:</span><span style="font-weight:700">${v}</span></div>`).join("")}
    </div>
    <div style="background:${rec.bg};border:2px solid ${rec.color}33;border-radius:8px;padding:12px">
      <div style="font-size:9px;color:#888;font-weight:700;margin-bottom:6px">SCREENING RESULT</div>
      <div style="font-size:15px;font-weight:900;color:${rec.color};margin-bottom:5px">${rec.label}</div>
      <div style="font-size:9px;line-height:1.6;color:#555">${rec.text}</div>
    </div>
  </div>

  <div style="display:flex;gap:8px;margin-bottom:16px">
    ${DOMAINS.map(d => { const sc = domainScores[d.id]; const fl = flags[d.id]; const fc = fl === "green" ? "#1A7A40" : fl === "amber" ? "#B06000" : "#C0392B"; const fb = fl === "green" ? "#E8F5EE" : fl === "amber" ? "#FEF3E2" : "#FDECEA"; return `<div style="flex:1;text-align:center;background:${fb};border-radius:8px;padding:8px;border:1px solid ${fc}33"><div style="font-size:16px">${d.icon}</div><div style="font-weight:900;font-size:13px;color:${fc}">${sc}%</div><div style="font-size:8px;color:${fc};margin-top:1px">${d.label}</div></div>` }).join("")}
  </div>

  <div style="margin-bottom:16px">
    <div style="font-size:12px;font-weight:800;color:#0B1F35;margin-bottom:6px;padding-bottom:4px;border-bottom:2px solid #E5E7EB">🔬 Skill Cluster Analysis</div>
    <p style="font-size:10px;color:#6B7280;margin-bottom:10px">Skills from across all five Panchakosha areas are grouped into 11 functional ability clusters. This helps identify exactly which everyday skills could use extra support.</p>
    <div style="display:grid;grid-template-columns:repeat(3,1fr);gap:8px;margin-bottom:12px">${clusterGridHTML}</div>
    <div style="display:grid;grid-template-columns:1fr 1fr 1fr;gap:10px;margin-bottom:14px">
      ${groupHTML("✅ Strengths", strengths, "#15803D", "#DCFCE7", "None at Strong level yet")}
      ${groupHTML("🔄 Developing", developing, "#B45309", "#FEF3C7", "None in this range")}
      ${groupHTML("⚠️ Needs Support", needs, "#B91C1C", "#FEE2E2", "No areas flagged")}
    </div>
    ${clusterNarrativesHTML}
  </div>

  <div class="page-break" style="margin-bottom:16px">
    <div style="font-size:12px;font-weight:800;color:#0B1F35;margin-bottom:6px;padding-bottom:4px;border-bottom:2px solid #E5E7EB">💡 Our Recommendations</div>
    <div style="background:#FFFBEA;border:1.5px solid #E8C84A;border-radius:9px;padding:12px 14px">${recsHTML}</div>
  </div>

  ${topNeeds.length > 0 ? `
  <div style="margin-bottom:16px">
    <div style="font-size:12px;font-weight:800;color:#0B1F35;margin-bottom:6px;padding-bottom:4px;border-bottom:2px solid #E5E7EB">🏠 Activities to Try at Home</div>
    <p style="font-size:10px;color:#6B7280;margin-bottom:10px">These activities target the areas where ${child?.name || "your child"} could use the most support right now. They use everyday materials and take only 10–15 minutes.</p>
    <div style="background:#F0FDF4;border-radius:10px;padding:12px 14px;border:1.5px solid #BBF7D0">${homeActivitiesHTML}</div>
  </div>` : ""}

  <div class="page-break">
    <div style="font-size:12px;font-weight:800;color:#0B1F35;margin-bottom:8px;padding-bottom:4px;border-bottom:2px solid #E5E7EB">📋 Detailed Item-by-Item Results</div>
    ${domainRows}
  </div>

  <div style="background:#FFFBEA;border:1px solid #E8C84A;border-radius:6px;padding:9px;margin-top:10px;font-size:8.5px;color:#7A6000">
    <strong>Note:</strong> This is a free, parent-administered quick screener. It is NOT a diagnostic tool.
    If the result suggests "Detailed Assessment Recommended", please share this report with your child's
    teacher or a qualified special educator.
  </div>
  <div style="margin-top:12px;border-top:1px solid #EEE;padding-top:8px;display:flex;justify-content:space-between;font-size:8px;color:#888">
    <span>Completed by (parent/guardian): ___________________________  Date: ${date}</span>
    <span>Vidyagni · NISH · NCAHT · ICMR</span>
  </div>
  <div class="no-print" style="text-align:center;padding:20px">
    <button onclick="window.print()" style="padding:12px 32px;background:#0B1F35;color:white;border:none;border-radius:9px;font-size:15px;font-weight:700;cursor:pointer">🖨️ Print / Save as PDF</button>
  </div>
  </body></html>`);
  win.document.close();
}

// ─── Small UI bits ───────────────────────────────────────────────────────────
function SignalRow({ signal, value, onChange }) {
  return (
    <div style={{ padding: "10px 0", borderBottom: `1px solid ${P.border}` }}>
      <div style={{ marginBottom: 8 }}>
        <div style={{ fontWeight: 700, fontSize: 13, color: P.ink, marginBottom: 2 }}>
          {signal.label}
        </div>
        <div style={{ fontSize: 11, color: P.muted, fontStyle: "italic" }}>{signal.hint}</div>
      </div>
      <div style={{ display: "grid", gridTemplateColumns: "repeat(4,1fr)", gap: 6 }}>
        {SIGNAL_LEVELS.map(lv => {
          const sel = value === lv.id;
          return (
            <button key={lv.id} onClick={() => onChange(sel ? null : lv.id)}
              style={{
                padding: "8px 4px", border: `2px solid ${sel ? lv.color : P.border}`,
                borderRadius: 9, background: sel ? lv.bg : P.white,
                cursor: "pointer", textAlign: "center", transition: "all .15s",
                fontFamily: "Outfit, sans-serif"
              }}>
              <div style={{ fontSize: 13, fontWeight: 900, color: sel ? lv.color : P.muted }}>{lv.short}</div>
              <div style={{ fontSize: 9, fontWeight: 700, color: sel ? lv.color : P.muted, marginTop: 1 }}>{lv.label}</div>
              <div style={{ fontSize: 8, color: sel ? lv.color : "#B0BEC5", marginTop: 1, lineHeight: 1.3 }}>{lv.desc}</div>
            </button>
          );
        })}
      </div>
    </div>
  );
}

// ─── Exam-style activity card — one activity shown at a time ───────────────
function ExamCard({ activity, ratings, onRate }) {
  const domain = DOMAINS.find(d => d.id === activity.domain);
  const rated = activity.signals.filter(s => ratings[s.id]).length;
  const total = activity.signals.length;

  return (
    <div style={{
      background: P.white, borderRadius: 16, border: `2px solid ${domain.color}`,
      boxShadow: `0 10px 36px ${domain.color}1a`, overflow: "hidden",
    }}>
      <div style={{ padding: "16px 20px", background: domain.bg, borderBottom: `1px solid ${domain.color}22`, display: "flex", alignItems: "center", gap: 12 }}>
        <div style={{
          width: 44, height: 44, borderRadius: 12, background: P.white,
          border: `2px solid ${domain.color}33`, display: "flex",
          alignItems: "center", justifyContent: "center", flexShrink: 0,
        }}>
          <span style={{ fontSize: 22 }}>{activity.icon}</span>
        </div>
        <div style={{ flex: 1, minWidth: 0 }}>
          <div style={{ display: "flex", alignItems: "center", gap: 7, marginBottom: 3 }}>
            <span style={{
              fontSize: 9, fontWeight: 700, color: domain.color,
              background: P.white, padding: "1px 7px",
              borderRadius: 5, border: `1px solid ${domain.color}33`
            }}>{domain.icon} {domain.label}</span>
            <span style={{ fontSize: 9, color: P.muted, background: P.white, padding: "1px 6px", borderRadius: 5, border: `1px solid ${P.border}` }}>⏱ {activity.duration}</span>
          </div>
          <div style={{ fontWeight: 800, fontSize: 16, color: domain.dark }}>
            Question {activity.seq} of 10 — {activity.title}
          </div>
          <div style={{ fontSize: 11, color: P.muted, marginTop: 1 }}>
            {activity.tagline}
          </div>
        </div>
        <div style={{ flexShrink: 0, textAlign: "right" }}>
          <div style={{ fontSize: 13, fontWeight: 800, color: rated === total ? P.green : rated > 0 ? P.amber : P.muted }}>
            {rated}/{total}
          </div>
          <div style={{ fontSize: 9, color: P.muted }}>signals rated</div>
        </div>
      </div>

      <div style={{ padding: "18px 20px 22px" }}>
        <div style={{ display: "grid", gridTemplateColumns: "1fr 1fr", gap: 10, marginBottom: 16 }}>
          <div style={{ background: "#F8FAFC", border: `1px solid ${P.border}`, borderRadius: 10, padding: "12px 14px" }}>
            <div style={{ fontSize: 9, fontWeight: 800, color: P.muted, letterSpacing: .5, marginBottom: 6 }}>🔧 SETUP</div>
            <div style={{ fontSize: 12, color: P.ink, lineHeight: 1.7 }}>{activity.setup}</div>
          </div>
          <div style={{ background: domain.bg, border: `1px solid ${domain.color}22`, borderRadius: 10, padding: "12px 14px" }}>
            <div style={{ fontSize: 9, fontWeight: 800, color: domain.color, letterSpacing: .5, marginBottom: 6 }}>💬 SAY TO YOUR CHILD</div>
            <div style={{ fontSize: 12, color: domain.dark, lineHeight: 1.7, fontStyle: "italic" }}>"{activity.instruction}"</div>
          </div>
        </div>

        <div style={{ background: "#FFFBEA", border: "1px solid #E8C84A", borderRadius: 9, padding: "9px 14px", marginBottom: 16, display: "flex", gap: 8 }}>
          <span style={{ fontSize: 14, flexShrink: 0 }}>👁️</span>
          <div>
            <div style={{ fontSize: 9, fontWeight: 800, color: "#7A6000", letterSpacing: .5, marginBottom: 3 }}>WHAT TO WATCH</div>
            <div style={{ fontSize: 12, color: "#5A4800", lineHeight: 1.6 }}>{activity.what_to_watch}</div>
          </div>
        </div>

        <div style={{ fontSize: 9, fontWeight: 800, color: P.muted, letterSpacing: .5, marginBottom: 4 }}>RATE EACH SIGNAL</div>
        {activity.signals.map(sig => (
          <SignalRow key={sig.id} signal={sig} value={ratings[sig.id] || null} onChange={val => onRate(sig.id, val)} />
        ))}
      </div>
    </div>
  );
}

// ─── Result view ─────────────────────────────────────────────────────────────
function ResultView({ child, ratings, clusterScores, date, onEdit, onRestart, activityTimes }) {
  const result = computeResult(ratings);
  const { domainScores, overall, flags, recommendation } = result;

  const REC_CONFIG = {
    "no-concerns": { icon: "✅", label: "No Concerns", color: P.green, bg: P.greenBg,
      headline: "Your child is developing well across all five areas observed today.",
      action: "No immediate action needed. Keep up everyday play and learning, and re-screen in about 6 months." },
    "monitor": { icon: "⚠️", label: "Monitor & Support", color: P.amber, bg: P.amberBg,
      headline: "Emerging skills observed in one or more areas.",
      action: "Continue regular practice at home and re-screen in 8–10 weeks. If you'd like a closer look, the full 90-item Panchakosha assessment is available through Vidyagni / NISH." },
    "full-srt": { icon: "🔴", label: "Detailed Assessment Recommended", color: P.red, bg: P.redBg,
      headline: "Two or more areas showed limited or delayed skills today.",
      action: "We recommend sharing this report with your child's teacher or a special educator, and considering the full 90-item Panchakosha assessment for a detailed developmental profile." },
  };
  const rec = REC_CONFIG[recommendation];
  const age = child?.dob ? `Age ${calcAge(child.dob)}` : "";
  const ratedCount = Object.values(ratings).filter(Boolean).length;
  const totalSignals = ACTIVITIES.reduce((s, a) => s + a.signals.length, 0);

  const assessedClusters = QS_CLUSTERS.map(c => ({ ...c, pct: clusterScores[c.id] })).filter(c => c.pct !== null);
  const topNeeds = [...assessedClusters].sort((a, b) => a.pct - b.pct).slice(0, 2);

  return (
    <div className="pqs-up" style={{ padding: "0 0 60px" }}>
      <div style={{ background: `linear-gradient(135deg, ${P.navy} 0%, #1A3A5C 100%)`, padding: "32px 20px", marginBottom: 24 }}>
        <div style={{ maxWidth: 760, margin: "0 auto" }}>
          <div style={{ fontSize: 10, fontWeight: 700, color: "rgba(255,255,255,.4)", letterSpacing: 1.5, marginBottom: 16 }}>
            FREE QUICK SCREENER — RESULT
          </div>
          <div style={{ background: rec.bg, borderRadius: 14, padding: "18px 22px", border: `2px solid ${rec.color}44`, marginBottom: 20, display: "flex", alignItems: "center", gap: 16, flexWrap: "wrap" }}>
            <div style={{ fontSize: 42, flexShrink: 0 }}>{rec.icon}</div>
            <div style={{ flex: 1, minWidth: 200 }}>
              <div style={{ fontWeight: 900, fontSize: 22, color: rec.color, fontFamily: "Lora, serif", marginBottom: 4 }}>{rec.label}</div>
              <div style={{ fontSize: 13, color: P.ink, lineHeight: 1.6, fontWeight: 500 }}>{rec.headline}</div>
            </div>
            <div style={{ textAlign: "center", flexShrink: 0 }}>
              <div style={{ fontSize: 44, fontWeight: 900, color: rec.color, fontFamily: "Lora, serif", lineHeight: 1 }}>{overall}%</div>
              <div style={{ fontSize: 10, color: P.muted, fontWeight: 600 }}>overall</div>
            </div>
          </div>

          <div style={{ display: "grid", gridTemplateColumns: "repeat(5,1fr)", gap: 8 }}>
            {DOMAINS.map(d => {
              const sc = domainScores[d.id];
              const fl = flags[d.id];
              const fc = fl === "green" ? P.green : fl === "amber" ? P.amber : P.red;
              const fb = fl === "green" ? P.greenBg : fl === "amber" ? P.amberBg : P.redBg;
              return (
                <div key={d.id} style={{ background: fb, border: `1.5px solid ${fc}33`, borderRadius: 14, padding: "14px 8px", textAlign: "center" }}>
                  <div style={{ fontSize: 28 }}>{d.icon}</div>
                  <div style={{ fontWeight: 900, fontSize: 22, color: fc, fontFamily: "Lora, serif", lineHeight: 1 }}>{sc}%</div>
                  <div style={{ fontSize: 11, color: fc, fontWeight: 700, marginTop: 2 }}>{d.label}</div>
                  <div style={{ fontSize: 10, marginTop: 4 }}>{fl === "green" ? "✅ OK" : fl === "amber" ? "⚠️ Monitor" : "🔴 Refer"}</div>
                </div>
              );
            })}
          </div>
        </div>
      </div>

      <div style={{ maxWidth: 760, margin: "0 auto", padding: "0 20px" }}>
        <div style={{ background: P.white, borderRadius: 14, padding: "20px 22px", border: `1.5px solid ${rec.color}33`, boxShadow: `0 4px 20px ${rec.color}12` }}>
          <div style={{ display: "flex", alignItems: "flex-start", gap: 14, flexWrap: "wrap" }}>
            <div style={{ width: 40, height: 40, borderRadius: 10, background: rec.bg, display: "flex", alignItems: "center", justifyContent: "center", fontSize: 20, flexShrink: 0 }}>📋</div>
            <div style={{ flex: 1, minWidth: 200 }}>
              <div style={{ fontWeight: 800, fontSize: 14, color: P.ink, marginBottom: 6 }}>What this means for you</div>
              <div style={{ fontSize: 13, color: P.ink, lineHeight: 1.8 }}>{rec.action}</div>
              <div style={{ marginTop: 12, display: "flex", gap: 8, flexWrap: "wrap" }}>
                <button onClick={() => printScreeningReport(child, ratings, result, clusterScores, date, activityTimes)}
                  style={{ padding: "9px 20px", background: rec.bg, color: rec.color, border: `1.5px solid ${rec.color}44`, borderRadius: 9, fontWeight: 700, fontSize: 13, cursor: "pointer", fontFamily: "Outfit, sans-serif" }}>
                  🖨️ Download / Print Report
                </button>
                <button onClick={onEdit}
                  style={{ padding: "9px 16px", background: P.fog, color: P.muted, border: `1.5px solid ${P.border}`, borderRadius: 9, fontWeight: 700, fontSize: 13, cursor: "pointer", fontFamily: "Outfit, sans-serif" }}>
                  ← Edit Ratings
                </button>
                <button onClick={onRestart}
                  style={{ padding: "9px 16px", background: P.fog, color: P.muted, border: `1.5px solid ${P.border}`, borderRadius: 9, fontWeight: 700, fontSize: 13, cursor: "pointer", fontFamily: "Outfit, sans-serif" }}>
                  ↺ Start a New Screening
                </button>
              </div>
            </div>
          </div>
        </div>

        <div style={{ background: P.fog, borderRadius: 10, padding: "12px 16px", marginTop: 14, display: "flex", gap: 16, flexWrap: "wrap", border: `1px solid ${P.border}`, fontSize: 12 }}>
          {[["Child", child?.name || "–"], ["Age", age || "–"], ["Date", date], ["Signals Rated", `${ratedCount} of ${totalSignals}`]].map(([k, v]) => (
            <div key={k} style={{ display: "flex", gap: 6 }}>
              <span style={{ color: P.muted, fontWeight: 600 }}>{k}:</span>
              <span style={{ fontWeight: 700, color: P.ink }}>{v}</span>
            </div>
          ))}
        </div>

        {/* ── Skill Cluster Analysis ────────────────────────────────────── */}
        <div style={{ marginTop: 22 }}>
          <div style={{ fontWeight: 800, fontSize: 15, color: P.ink, marginBottom: 4 }}>🔬 Skill Cluster Analysis</div>
          <div style={{ fontSize: 12, color: P.muted, marginBottom: 12, lineHeight: 1.6 }}>
            Today's 50 signals are grouped into 11 functional skill areas. The full report (print/download above) includes
            a description for each area plus suggested home activities.
          </div>
          <div style={{ display: "grid", gridTemplateColumns: "repeat(auto-fit, minmax(180px, 1fr))", gap: 10 }}>
            {QS_CLUSTERS.map(c => {
              const pct = clusterScores[c.id];
              const clv = levelFor(pct);
              const ratedN = c.items.filter(id => ratings[id]).length;
              return (
                <div key={c.id} style={{ background: P.white, border: `1.5px solid ${P.border}`, borderRadius: 12, padding: "12px 14px", opacity: pct === null ? 0.55 : 1 }}>
                  <div style={{ height: 4, background: pct !== null ? c.color : P.border, borderRadius: 4, marginBottom: 8, marginLeft: -14, marginRight: -14, marginTop: -12 }} />
                  <div style={{ display: "flex", justifyContent: "space-between", alignItems: "flex-start" }}>
                    <span style={{ fontSize: 12, fontWeight: 800, color: c.color }}>{c.icon} {c.label}</span>
                    <span style={{ fontSize: 18, fontWeight: 900, color: clv.color, flexShrink: 0, marginLeft: 6 }}>{pct !== null ? `${pct}%` : "—"}</span>
                  </div>
                  <div style={{ height: 6, background: P.border, borderRadius: 4, overflow: "hidden", margin: "8px 0 6px" }}>
                    <div style={{ width: `${pct ?? 0}%`, height: "100%", background: pct !== null ? c.color : P.border, borderRadius: 4 }} />
                  </div>
                  <div style={{ display: "flex", justifyContent: "space-between", alignItems: "center" }}>
                    <span style={{ background: clv.bg, color: clv.color, fontSize: 10, fontWeight: 700, padding: "1px 8px", borderRadius: 6 }}>{clv.label}</span>
                    <span style={{ fontSize: 10, color: P.muted }}>{ratedN}/{c.items.length}</span>
                  </div>
                </div>
              );
            })}
          </div>
        </div>

        {topNeeds.length > 0 && (
          <div style={{ marginTop: 22 }}>
            <div style={{ fontWeight: 800, fontSize: 15, color: P.ink, marginBottom: 4 }}>🏠 A Couple of Things to Try at Home</div>
            <div style={{ fontSize: 12, color: P.muted, marginBottom: 12 }}>Based on today's screening, these areas could use a little extra everyday practice.</div>
            <div style={{ display: "grid", gridTemplateColumns: "repeat(auto-fit, minmax(280px, 1fr))", gap: 12 }}>
              {topNeeds.map(c => {
                const detail = CLUSTER_DETAIL[c.id];
                return (
                  <div key={c.id} style={{ background: P.greenBg, border: "1.5px solid #BBF7D0", borderRadius: 12, padding: "14px 16px" }}>
                    <div style={{ display: "flex", alignItems: "center", gap: 9, marginBottom: 8 }}>
                      <div style={{ width: 32, height: 32, borderRadius: 8, background: c.bg, display: "flex", alignItems: "center", justifyContent: "center", fontSize: 16 }}>{c.icon}</div>
                      <div style={{ fontWeight: 800, fontSize: 13, color: c.color }}>{c.label}</div>
                    </div>
                    {detail?.activities?.map((act, i) => (
                      <div key={i} style={{ marginBottom: 6 }}>
                        <div style={{ fontSize: 12, fontWeight: 700, color: "#166534" }}>{i + 1}. {act.title}</div>
                        <div style={{ fontSize: 11, color: "#374151", lineHeight: 1.6 }}>{act.desc}</div>
                      </div>
                    ))}
                  </div>
                );
              })}
            </div>
          </div>
        )}

        <div style={{ background: "#FFFBEA", border: "1.5px solid #E8C84A", borderRadius: 10, padding: 14, marginTop: 22 }}>
          <div style={{ fontSize: 11, fontWeight: 800, color: "#B08000", marginBottom: 5 }}>📌 Important Notice</div>
          <p style={{ fontSize: 11, lineHeight: 1.8, color: "#555", margin: 0 }}>
            This free quick screener captures approximate developmental signals through 10 short activities
            (~15–20 minutes). It is <strong>not a diagnostic tool</strong> and does not replace a comprehensive
            assessment by a qualified professional. Please discuss this report with your child's teacher,
            paediatrician, or a special educator.
          </p>
        </div>
      </div>
    </div>
  );
}

// ─── Main page ────────────────────────────────────────────────────────────────
export default function PublicQuickScreener() {
  useEffect(() => { injectCSS(); }, []);
  const navigate = useNavigate();

  const [step, setStep] = useState("intro"); // intro | screen | result
  const [child, setChild] = useState({ name: "", dob: "", notes: "" });
  const [consentChecked, setConsentChecked] = useState(false);
  const [date] = useState(new Date().toISOString().split("T")[0]);
  const [activeIndex, setActiveIndex] = useState(0);
  const [direction, setDirection] = useState("right");
  const [ratings, setRatings] = useState({});

  // ── Silent timing tracking (never rendered) ────────────────────────────
  const [activityTimes, setActivityTimes] = useState({});
  const enterTimeRef = useRef(Date.now());

  useEffect(() => {
    if (step === "screen") enterTimeRef.current = Date.now();
  }, [step]);

  const flushTime = useCallback(() => {
    const id = ACTIVITIES[activeIndex].id;
    const elapsed = (Date.now() - enterTimeRef.current) / 1000;
    setActivityTimes(prev => ({ ...prev, [id]: (prev[id] || 0) + elapsed }));
    enterTimeRef.current = Date.now();
  }, [activeIndex]);

  const rateSignal = useCallback((signalId, levelId) => {
    setRatings(prev => ({ ...prev, [signalId]: levelId }));
  }, []);

  const totalSignals = ACTIVITIES.reduce((s, a) => s + a.signals.length, 0);
  const ratedSignals = Object.values(ratings).filter(Boolean).length;
  const pctDone = Math.round((ratedSignals / totalSignals) * 100);
  const actComplete = act => act.signals.every(s => ratings[s.id]);
  const completedCount = ACTIVITIES.filter(actComplete).length;

  const isFirst = activeIndex === 0;
  const isLast = activeIndex === ACTIVITIES.length - 1;
  const currentAct = ACTIVITIES[activeIndex];

  const goPrev = () => {
    if (isFirst) return;
    flushTime();
    setDirection("left");
    setActiveIndex(i => i - 1);
  };

  const goNext = () => {
    if (isLast) return;
    flushTime();
    setDirection("right");
    setActiveIndex(i => i + 1);
  };

  const jumpTo = (idx) => {
    if (idx === activeIndex) return;
    flushTime();
    setDirection(idx > activeIndex ? "right" : "left");
    setActiveIndex(idx);
  };

  const finishScreening = () => {
    flushTime();
    setStep("result");
  };

  const restart = () => {
    setRatings({});
    setActivityTimes({});
    setActiveIndex(0);
    setDirection("right");
    setStep("intro");
  };

  const clusterScores = computeClusterScores(ratings);

  return (
    <div style={{ fontFamily: "Outfit, sans-serif", background: P.fog, minHeight: "100vh" }}>
      {/* ── Header ── */}
      <div style={{ background: P.navy, color: "white", padding: "0 20px", height: 58, display: "flex", alignItems: "center", justifyContent: "space-between", boxShadow: "0 2px 14px rgba(0,0,0,.25)", position: "sticky", top: 0, zIndex: 50 }}>
        <div style={{ display: "flex", alignItems: "center", gap: 10, cursor: "pointer" }} onClick={() => navigate("/")}>
          <div style={{ fontFamily: "Lora, serif", fontSize: 18, fontWeight: 900, color: P.gold }}>Vidyagni</div>
          <div style={{ width: 1, height: 18, background: "rgba(255,255,255,.2)" }} />
          <span style={{ fontSize: 12, fontWeight: 700, color: "rgba(255,255,255,.8)" }}>Free Quick Screener</span>
        </div>
        <button onClick={() => navigate("/")}
          style={{ background: "rgba(255,255,255,.1)", color: "white", border: "1px solid rgba(255,255,255,.2)", borderRadius: 8, padding: "6px 14px", fontSize: 12, fontWeight: 700, cursor: "pointer", fontFamily: "Outfit, sans-serif" }}>
          ← Back to Vidyagni
        </button>
      </div>

      {/* ── INTRO ── */}
      {step === "intro" && (
        <div className="pqs-up" style={{ maxWidth: 720, margin: "0 auto", padding: "40px 20px 60px" }}>
          <div style={{ textAlign: "center", marginBottom: 28 }}>
            <div style={{ fontSize: 40, marginBottom: 8 }}>🎮</div>
            <h1 style={{ fontFamily: "Lora, serif", fontSize: 28, fontWeight: 900, color: P.navy, marginBottom: 8 }}>
              Free School Readiness Quick Screener
            </h1>
            <p style={{ fontSize: 14, color: P.muted, lineHeight: 1.8, maxWidth: 540, margin: "0 auto" }}>
              10 short, playful activities (about 15–20 minutes total) covering all five Panchakosha
              developmental domains — Physical, Socio-Emotional, Cognitive, Language &amp; Numeracy, and
              Creativity. No login needed. Your responses stay on this device.
            </p>
          </div>

          <div style={{ display: "flex", gap: 8, justifyContent: "center", marginBottom: 28, flexWrap: "wrap" }}>
            {[["NISH", NISHLogo], ["NCAHT", NCAHTLogo], ["ICMR", ICMRLogo]].map(([label, src]) => (
              <div key={label} style={{ background: "white", border: `1.5px solid ${P.border}`, borderRadius: 12, padding: "10px 16px", display: "flex", alignItems: "center" }}>
                <img src={src} alt={label} height={28} />
              </div>
            ))}
          </div>

          <div style={{ background: "white", borderRadius: 14, border: `1.5px solid ${P.border}`, boxShadow: "0 2px 12px rgba(0,0,0,.05)", marginBottom: 18, overflow: "hidden" }}>
            <div style={{ padding: "16px 24px 0" }}>
              <div style={{ fontWeight: 800, fontSize: 14, color: P.navy, marginBottom: 4 }}>📄 Parental Consent</div>
              <div style={{ fontSize: 11, color: P.muted, marginBottom: 10 }}>Please read before starting. Scroll to see the full form.</div>
            </div>
            <div style={{ maxHeight: 260, overflowY: "auto", padding: "0 24px 16px", borderBottom: `1px solid ${P.border}` }}>
              {CONSENT_SECTIONS.map(sec => (
                <div key={sec.title} style={{ marginBottom: 14 }}>
                  <div style={{ fontWeight: 700, fontSize: 12, color: P.ink, marginBottom: 4 }}>{sec.title}</div>
                  <div style={{ fontSize: 12, color: P.muted, lineHeight: 1.8 }}>{sec.body}</div>
                </div>
              ))}
              <div style={{ fontSize: 10, color: "#A0B0BC", marginTop: 10, paddingTop: 10, borderTop: `1px dashed ${P.border}` }}>
                Title: School Readiness Assessment Test – Vidyagni FSLP · Principal Investigator: Dr Suja K Kunnath, NCAHT–NISH · Co-Investigators: Amith G Nair, Arya M S, NCAHT–NISH
              </div>
            </div>
            <label style={{
              display: "flex", alignItems: "flex-start", gap: 10, padding: "12px 24px",
              background: consentChecked ? P.greenBg : P.fog, cursor: "pointer",
              borderTop: consentChecked ? `1px solid ${P.green}33` : "none",
              position: "sticky", bottom: 0, zIndex: 1, transition: "background .15s"
            }}>
              <input type="checkbox" checked={consentChecked}
                onChange={e => setConsentChecked(e.target.checked)}
                style={{ width: 18, height: 18, marginTop: 1, flexShrink: 0, accentColor: P.green, cursor: "pointer" }} />
              <span style={{ fontSize: 12, color: P.ink, lineHeight: 1.6, fontWeight: 600 }}>
                I have read and understood the consent form above. I voluntarily agree to participate
                in this free screening on behalf of my child.
              </span>
            </label>
          </div>

          <div style={{ display: "grid", gridTemplateColumns: "1fr 1fr", gap: 12, marginBottom: 12 }}>
            <div>
              <label style={{ fontSize: 11, fontWeight: 700, color: P.muted, display: "block", marginBottom: 5 }}>Child's name</label>
              <input value={child.name} onChange={e => setChild(c => ({ ...c, name: e.target.value }))}
                placeholder="e.g. Riya"
                style={{ width: "100%", padding: "9px 12px", border: `1.5px solid ${P.border}`, borderRadius: 8, fontSize: 13, outline: "none", fontFamily: "Outfit, sans-serif", boxSizing: "border-box" }} />
            </div>
            <div>
              <label style={{ fontSize: 11, fontWeight: 700, color: P.muted, display: "block", marginBottom: 5 }}>Date of birth</label>
              <input type="date" value={child.dob} onChange={e => setChild(c => ({ ...c, dob: e.target.value }))}
                style={{ width: "100%", padding: "9px 12px", border: `1.5px solid ${P.border}`, borderRadius: 8, fontSize: 13, outline: "none", fontFamily: "Outfit, sans-serif", boxSizing: "border-box" }} />
            </div>
          </div>
          <div style={{ marginBottom: 18 }}>
            <label style={{ fontSize: 11, fontWeight: 700, color: P.muted, display: "block", marginBottom: 5 }}>Anything you'd like to note (optional)</label>
            <input value={child.notes} onChange={e => setChild(c => ({ ...c, notes: e.target.value }))}
              placeholder="e.g. speech delay noticed, hearing aid user…"
              style={{ width: "100%", padding: "9px 12px", border: `1.5px solid ${P.border}`, borderRadius: 8, fontSize: 13, outline: "none", fontFamily: "Outfit, sans-serif", boxSizing: "border-box" }} />
          </div>
          <button onClick={() => consentChecked && setStep("screen")} disabled={!consentChecked}
            style={{
              width: "100%",
              background: consentChecked ? `linear-gradient(135deg,${P.navy},#1A3A5C)` : "#D7DEE6",
              color: consentChecked ? "white" : "#9AA7B5",
              border: "none", borderRadius: 11, padding: "13px 0", fontSize: 14, fontWeight: 800,
              cursor: consentChecked ? "pointer" : "not-allowed", fontFamily: "Outfit, sans-serif"
            }}>
            ⚡ Start Screening
          </button>
          {!consentChecked && (
            <div style={{ fontSize: 11, color: P.muted, textAlign: "center", marginTop: 8 }}>
              Please read and accept the consent form above to continue.
            </div>
          )}
        </div>
      )}

      {/* ── SCREEN (exam-style, one activity at a time) ── */}
      {step === "screen" && (
        <div style={{ maxWidth: 780, margin: "0 auto", padding: "18px 16px 110px" }}>
          {/* Top bar: child info + per-domain dot progress */}
          <div style={{ background: "white", borderRadius: 12, padding: "11px 16px", marginBottom: 14, display: "flex", alignItems: "center", gap: 10, border: `1.5px solid ${P.border}`, boxShadow: "0 1px 6px rgba(0,0,0,.05)", flexWrap: "wrap" }}>
            <div style={{ width: 36, height: 36, borderRadius: 9, flexShrink: 0, background: P.sky, display: "flex", alignItems: "center", justifyContent: "center", color: "white", fontWeight: 900, fontSize: 15 }}>
              {child.name ? child.name[0].toUpperCase() : "🙂"}
            </div>
            <div style={{ flex: 1, minWidth: 100 }}>
              <div style={{ fontWeight: 800, fontSize: 13, color: P.ink }}>{child.name || "Your child"}</div>
              <div style={{ fontSize: 10, color: P.muted }}>{calcAge(child.dob) != null ? `Age ${calcAge(child.dob)}` : "10 questions · ~15–20 min"}</div>
            </div>
            <div style={{ fontSize: 11, fontWeight: 700, color: P.navy, background: P.fog, padding: "4px 10px", borderRadius: 7, border: `1px solid ${P.border}` }}>
              Question {activeIndex + 1} / {ACTIVITIES.length}
            </div>
          </div>

          {/* Question dot navigator */}
          <div style={{ display: "flex", justifyContent: "center", gap: 6, marginBottom: 16, flexWrap: "wrap" }}>
            {ACTIVITIES.map((act, idx) => {
              const d = DOMAINS.find(dm => dm.id === act.domain);
              const done = actComplete(act);
              const isCurrent = idx === activeIndex;
              return (
                <button key={act.id} onClick={() => jumpTo(idx)} title={`${idx + 1}. ${act.title}`}
                  style={{
                    width: isCurrent ? 30 : 24, height: isCurrent ? 30 : 24, borderRadius: "50%",
                    border: `2px solid ${isCurrent ? d.color : done ? d.color + "88" : P.border}`,
                    background: done ? d.bg : "white",
                    color: isCurrent ? d.dark : done ? d.color : P.muted,
                    fontSize: 11, fontWeight: 800, cursor: "pointer",
                    display: "flex", alignItems: "center", justifyContent: "center",
                    transition: "all .15s", flexShrink: 0,
                    boxShadow: isCurrent ? `0 0 0 4px ${d.color}22` : "none",
                  }}>
                  {done ? "✓" : idx + 1}
                </button>
              );
            })}
          </div>

          <div style={{ background: "#EBF4FA", border: "1.5px solid #BDD7EE", borderRadius: 10, padding: "10px 16px", marginBottom: 16, display: "flex", gap: 10 }}>
            <span style={{ fontSize: 15, flexShrink: 0 }}>💡</span>
            <div style={{ fontSize: 11, color: "#1A6FAA", lineHeight: 1.7 }}>
              <strong>How to use:</strong> Set up the materials for this activity, say the instruction to
              your child, and rate what you observe. Use the arrows below to move to the next or previous
              activity — you can come back and change ratings at any time before viewing your result.
            </div>
          </div>

          {/* Exam-style sliding card */}
          <div key={activeIndex} className={direction === "right" ? "pqs-slide-right" : "pqs-slide-left"}>
            <ExamCard activity={currentAct} ratings={ratings} onRate={rateSignal} />
          </div>

          {/* Bottom nav bar */}
          <div style={{ position: "sticky", bottom: 12, background: "white", border: `1.5px solid ${P.border}`, borderRadius: 12, padding: "10px 16px", marginTop: 14, display: "flex", alignItems: "center", gap: 10, boxShadow: "0 -4px 20px rgba(0,0,0,.07)" }}>
            <button onClick={goPrev} disabled={isFirst}
              style={{ background: isFirst ? "#F1F4F8" : P.fog, color: isFirst ? "#C2CCD6" : P.ink, border: `1.5px solid ${P.border}`, borderRadius: 8, padding: "9px 16px", fontWeight: 800, fontSize: 12, cursor: isFirst ? "not-allowed" : "pointer", fontFamily: "Outfit, sans-serif" }}>
              ← Previous
            </button>

            <div style={{ flex: 1, display: "flex", alignItems: "center", gap: 10 }}>
              <div style={{ flex: 1, height: 5, background: P.border, borderRadius: 5, overflow: "hidden" }}>
                <div style={{ width: `${pctDone}%`, height: "100%", background: "#2C7BB6", borderRadius: 5, transition: "width .3s" }} />
              </div>
              <span style={{ fontSize: 11, color: P.muted, whiteSpace: "nowrap" }}>{ratedSignals}/{totalSignals} signals · {completedCount}/10 activities</span>
            </div>

            {isLast ? (
              <button onClick={finishScreening} disabled={ratedSignals === 0}
                style={{ background: ratedSignals > 0 ? P.navy : "#ccc", color: "white", border: "none", borderRadius: 8, padding: "9px 18px", fontWeight: 800, fontSize: 12, cursor: ratedSignals > 0 ? "pointer" : "not-allowed", fontFamily: "Outfit, sans-serif" }}>
                📊 View Result
              </button>
            ) : (
              <button onClick={goNext}
                style={{ background: P.navy, color: "white", border: "none", borderRadius: 8, padding: "9px 18px", fontWeight: 800, fontSize: 12, cursor: "pointer", fontFamily: "Outfit, sans-serif" }}>
                Next →
              </button>
            )}
          </div>
        </div>
      )}

      {/* ── RESULT ── */}
      {step === "result" && (
        <ResultView child={child} ratings={ratings} clusterScores={clusterScores} date={date}
          onEdit={() => setStep("screen")} onRestart={restart} activityTimes={activityTimes} />
      )}
    </div>
  );
}